import { BoardFacts, PieceFacts, Session, Square } from "src/api";
import { TypedEventEmitter, createTypedEventEmitter } from "src/shared/util";

import { WorldObjectAPI } from "../render/world/WorldObject";

export type BoardInteractionTarget = Readonly<{
  domain: "3d";
  key: string;
  square: Square;
  worldObject?: WorldObjectAPI;
}>;

export type UIInteractionTaget = Readonly<{
  domain: "ui";
  key: string;
}>;

export type InteractionTarget = BoardInteractionTarget | UIInteractionTaget;

export function isBoardInteractionTarget(
  target: InteractionTarget | null
): target is BoardInteractionTarget {
  if (target === null) return false;
  return target.domain === "3d";
}

export function isUIInteractionTarget(
  target: InteractionTarget | null
): target is UIInteractionTaget {
  if (target === null) return false;
  return target.domain === "ui";
}

export function isSameInteractionTarget(
  t1: InteractionTarget | null,
  t2: InteractionTarget | null
) {
  if (t1 === null && t2 === null) return true;
  if (t1 === null || t2 === null) return false;
  if (t1.domain !== t2.domain) return false;
  return t1.key === t2.key;
}

export type CentralInteractionEvents = {
  hover: InteractionTarget | null;
  click: InteractionTarget | null;
  selected: InteractionTarget | null;
};

export type CentralInteractionAPI = {
  readonly events: TypedEventEmitter<CentralInteractionEvents>;
  doHover: (target: InteractionTarget | null) => void;
  doClick: (target: InteractionTarget | null) => void;
  getHover(): InteractionTarget | null;
  getSelection(): InteractionTarget | null;
};

export function keyForPiece(square: Square, piece: PieceFacts): string {
  return `${square}:${piece.side}:${piece.type}:${piece.variant}`;
}

export function keyForSquare(square: Square): string {
  return `${square}`;
}

export function keyForUniqueUIElement(uniqueKey: string): string {
  return uniqueKey;
}

export function keyForPieceUIElement(
  square: Square,
  piece: PieceFacts,
  elementKey: string
) {
  return `${keyForPiece(square, piece)}:${elementKey}`;
}

/**
 * This class exists to bridge the gap between the 2D and 3D interaction scopes.
 */
export class CentralInteractionSingleton implements CentralInteractionAPI {
  public readonly events = createTypedEventEmitter<CentralInteractionEvents>();

  private hoverTarget: InteractionTarget | null = null;
  private selectionTarget: InteractionTarget | null = null;
  private session?: Session;
  private sessionUnsubscribe?: () => void;
  private boardFacts?: BoardFacts;

  // Public API methods.
  doHover(target: InteractionTarget | null) {
    if (isSameInteractionTarget(this.hoverTarget, target)) return;
    if (isBoardInteractionTarget(this.hoverTarget)) {
      const details = this.hoverTarget.worldObject?.getSessionDetails?.();
      if (details) {
        details.hovered = false;
        this.hoverTarget.worldObject?.setSessionDetails?.(details);
      }
    }
    this.hoverTarget = target;
    if (isBoardInteractionTarget(this.hoverTarget)) {
      const details = this.hoverTarget.worldObject?.getSessionDetails?.();
      if (details) {
        details.hovered = true;
        this.hoverTarget.worldObject?.setSessionDetails?.(details);
      }
    }
    this.events.emit("hover", target);
  }
  doClick(target: InteractionTarget | null) {
    if (isSameInteractionTarget(this.selectionTarget, target)) return;
    if (isBoardInteractionTarget(this.selectionTarget)) {
      const details = this.selectionTarget.worldObject?.getSessionDetails?.();
      if (details) {
        details.selected = false;
        this.selectionTarget.worldObject?.setSessionDetails?.(details);
      }
    }
    this.selectionTarget = target;

    if (isBoardInteractionTarget(this.selectionTarget)) {
      this.session?.tap(this.selectionTarget.square);
      const details = this.selectionTarget.worldObject?.getSessionDetails?.();
      if (details) {
        details.selected = true;
        this.selectionTarget.worldObject?.setSessionDetails?.(details);
      }
    }
    this.events.emit("click", target);
    this.events.emit("selected", target);
  }
  getHover() {
    return this.hoverTarget;
  }
  getSelection() {
    return this.selectionTarget;
  }

  // Public session linkage methods.
  onSessionUpdate = () => {
    if (!this.session || !this.boardFacts) {
      this.doClick(null);
      return;
    }
    const sessionTarget = resolveSelectedInteractionTargetFromSession(
      this.session,
      this.boardFacts
    );
    this.doClick(sessionTarget);
  };
  setSession(session: Session) {
    if (this.session !== undefined) this.clearSession();
    this.session = session;
    this.sessionUnsubscribe = this.session.subscribe(this.onSessionUpdate);
  }
  clearSession() {
    this.sessionUnsubscribe?.();
    this.sessionUnsubscribe = undefined;
    this.session = undefined;
  }
  setBoardFacts(boardFacts: BoardFacts) {
    this.boardFacts = boardFacts;
  }
  clearBoardFacts() {
    this.boardFacts = undefined;
  }
}

// Updates the interaction target to match the value provided by the session.
export function resolveSelectedInteractionTargetFromSession(
  session: Session,
  boardFacts?: BoardFacts
): BoardInteractionTarget | null {
  const view = session.view();
  if (view.selected === undefined) {
    return null;
  }
  const squareFacts = boardFacts?.squares.get(view.selected);
  if (squareFacts === undefined) {
    return {
      domain: "3d",
      key: keyForSquare(view.selected),
      square: view.selected
    };
  }
  if (squareFacts.piece) {
    return {
      domain: "3d",
      key: keyForPiece(view.selected, squareFacts.piece),
      square: view.selected
    };
  }
  return {
    domain: "3d",
    key: keyForSquare(view.selected),
    square: view.selected
  };
}

export const kCentralInteractions = new CentralInteractionSingleton();
